import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { MethodSignature } from '@/db/schema/problems'

interface MethodSignaturesEditorProps {
  methods: MethodSignature[]
  onChange: (methods: MethodSignature[]) => void
}

export function MethodSignaturesEditor({
  methods,
  onChange,
}: MethodSignaturesEditorProps) {
  const updateMethod = (index: number, patch: Partial<MethodSignature>) => {
    onChange(methods.map((m, i) => (i === index ? { ...m, ...patch } : m)))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Method Signatures</h3>
          <p className="text-muted-foreground text-xs">
            Declares each method&apos;s parameter and return types. Use{' '}
            <code className="font-mono">void</code> when the return value should
            be ignored. Required to publish class-based problems.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            onChange([...methods, { name: '', params: [], returns: 'void' }])
          }
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Method
        </Button>
      </div>

      {methods.map((method, mi) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable id; index mirrors sibling editors
          key={mi}
          className="border-border bg-muted/30 space-y-2 rounded-lg border p-3"
        >
          <div className="flex items-end gap-3">
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Method Name
              </label>
              <Input
                value={method.name}
                onChange={(e) => updateMethod(mi, { name: e.target.value })}
                placeholder="e.g., get"
                className="max-w-[180px] font-mono text-sm"
              />
            </div>
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Returns
              </label>
              <Input
                value={method.returns}
                onChange={(e) => updateMethod(mi, { returns: e.target.value })}
                placeholder="void"
                className="max-w-[140px] font-mono text-sm"
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label="Remove method"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onChange(methods.filter((_, i) => i !== mi))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-1">
            {method.params.map((param, pi) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: params have no stable id; order is positional
                key={pi}
                className="flex items-center gap-2 pl-4"
              >
                <Input
                  value={param.name}
                  onChange={(e) =>
                    updateMethod(mi, {
                      params: method.params.map((p, i) =>
                        i === pi ? { ...p, name: e.target.value } : p,
                      ),
                    })
                  }
                  placeholder="param name"
                  className="max-w-[140px] text-sm"
                />
                <Input
                  value={param.type}
                  onChange={(e) =>
                    updateMethod(mi, {
                      params: method.params.map((p, i) =>
                        i === pi ? { ...p, type: e.target.value } : p,
                      ),
                    })
                  }
                  placeholder="integer"
                  className="max-w-[140px] font-mono text-sm"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label="Remove param"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() =>
                    updateMethod(mi, {
                      params: method.params
                        .filter((_, i) => i !== pi)
                        .map((p, i) => ({ ...p, order: i })),
                    })
                  }
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-muted-foreground ml-4"
              onClick={() =>
                updateMethod(mi, {
                  params: [
                    ...method.params,
                    {
                      name: `arg${method.params.length}`,
                      type: 'integer',
                      order: method.params.length,
                    },
                  ],
                })
              }
            >
              <Plus className="mr-1 h-3 w-3" />
              Add Param
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}
